// Single source of truth for every HTTP path in the app.
//
// Each entry is a builder: call it to get the absolute, percent-encoded URL the
// web client and CLI send (`routes.runs.detail({ slug })` -> `/api/runs/abc`).
// The same entry carries `.pattern` -- the Hono route pattern (with `:params`)
// the server registers. Because both are derived from one declaration, the
// client URL and the server route cannot drift.
//
// Every API router mounts at "/" on the `/api` Hono app, so `.pattern` is the
// full path *under* `/api` (e.g. "/runs/:slug"). See apps/api/src/index.ts.
// `.pattern` is typed as the literal pattern so Hono can infer `c.req.param`.

const API_PREFIX = "/api"
const enc = encodeURIComponent

// Param names embedded in a pattern: ParamKeys<"/:slug/x/:id"> = "slug" | "id".
type ParamKeys<S extends string> = S extends `${infer Head}/${infer Tail}`
  ? SegParam<Head> | ParamKeys<Tail>
  : SegParam<S>
type SegParam<Seg extends string> = Seg extends `:${infer Name}` ? Name : never

// No params -> zero-arg builder; otherwise require an object of the values.
type Builder<P extends string> = [ParamKeys<P>] extends [never]
  ? () => string
  : (params: Record<ParamKeys<P>, string>) => string

export type Route<P extends string = string> = Builder<P> & {
  /** Hono route pattern under `/api`, e.g. "/runs/:slug". */
  readonly pattern: P
}

function makeRoute<P extends string>(fullPattern: P): Route<P> {
  const build = ((params?: Record<string, string>): string =>
    `${API_PREFIX}${fullPattern}`.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
      const value = params?.[name]
      if (value === undefined) {
        throw new Error(`route ${fullPattern}: missing param "${name}"`)
      }
      return enc(value)
    })) as Builder<P>
  return Object.assign(build, { pattern: fullPattern })
}

// Build a group of routes sharing a `base` segment. `base` doubles as the path
// the owning Hono router contributes, so each route's `.pattern` is `base + rel`
// -- the full path under `/api`. Use "" for a group's index route.
function group<const B extends string, const T extends Record<string, string>>(
  base: B,
  rels: T
): { readonly [K in keyof T]: Route<`${B}${T[K]}`> } {
  const entries = Object.entries(rels).map(
    ([key, rel]) => [key, makeRoute(`${base}${rel}`)] as const
  )
  return Object.fromEntries(entries) as {
    readonly [K in keyof T]: Route<`${B}${T[K]}`>
  }
}

export const routes = {
  listingAutomation: group("/listing-automation", {
    list: "",
    update: "/:marketplace",
    revisions: "/:marketplace/revisions",
  }),
  outboundEvents: group("/outbound-events", {
    queue: "/queue",
    list: "",
    status: "/status",
    detail: "/events/:eventId",
    dispatch: "/dispatch",
    cutover: "/nutrition/cutover",
  }),
  auth: group("/auth", {
    me: "/me",
  }),
  ops: group("/ops", {
    magentoProbe: "/magento-probe",
  }),
  pimEvents: group("/pim-events", {
    status: "/status",
    nutritionQueue: "/nutrition-queue",
    history: "/history",
    historyDetail: "/history/:receiptId",
    deliverySettlementPreview: "/delivery-settlement/preview",
    deliverySettlementExecute: "/delivery-settlement/execute",
    deliverySettlementDetail: "/delivery-settlement/:deliveryId",
    recoveryPreview: "/recovery/preview",
    recoveryExecute: "/recovery/execute",
    recoveryVerify: "/recovery/:recoveryId/verify",
    recoveryDetail: "/recovery/:recoveryId",
  }),
  pimEventIngress: group("/internal/pim-events", {
    push: "/push",
  }),
  runs: group("/runs", {
    list: "",
    create: "",
    fromCategory: "/from-category",
    automaticInbox: "/automatic-inbox",
    relistQueue: "/relist-queue",
    preparationQueue: "/preparation-queue",
    detail: "/:slug",
    status: "/:slug/status",
    submission: "/:slug/submission",
    submissionPreflight: "/:slug/submission/preflight",
    submissionControl: "/:slug/submission/control",
    remove: "/:slug",
    // Per-run quality ledger: OK/NOK buckets plus feed-build/import verdict
    // audit aggregates. Read-only.
    qualityScorecard: "/:slug/quality-scorecard",
    // Per-run readiness diagnostics: classify active configs into ready /
    // business_excluded / preparation_defect and group defects by deterministic
    // root cause + owner (writer/category/enum/image/source/schema/offer).
    // Read-only. The compiler backstop for "which shared-layer fix clears the
    // most NOK" — see skills/operational/run-healing.md.
    diagnose: "/:slug/diagnose",
    // Durable, reattachable twins of `validate`/`diagnose` (issue #117). A
    // full-run compile can outlive the request socket (run-39 validate ~411s),
    // so the CLI starts a background job (`*Jobs` POST → 202 + job id) and polls
    // (`*Job` GET) until terminal, reattaching to an in-flight job on re-request.
    // The inline `validate`/`diagnose` above stay for the web + small runs.
    // Static suffixes, registered before `:slug` so they aren't swallowed.
    validateJobs: "/:slug/validate-jobs",
    validateJob: "/:slug/validate-jobs/:id",
    diagnoseJobs: "/:slug/diagnose-jobs",
    diagnoseJob: "/:slug/diagnose-jobs/:id",
    // Per-run reconciliation: bucketed drops + freshness/truncation health.
    reconcile: "/:slug/reconcile",
    repairCategoryWalk: "/:slug/category-walk/repair",
    // Category facets for the run page filters: distinct marketplace + Magento
    // categories present in the run, with counts. Read-only.
    facets: "/:slug/facets",
    configSkus: "/:slug/config-skus",
    products: "/:slug/products",
    preconditions: "/:slug/preconditions",
    skuPreview: "/:slug/skus/:configSku/preview",
    feedPreview: "/:slug/feed-preview",
    // Build the run's OF01 offer CSV. New-only by default (diffs the live
    // marketplace offer snapshot); `?mode=all` reproduces the push-everything
    // behaviour. Build only, no upload.
    offerFeed: "/:slug/offer-feed",
    // Initial Octopia (Cdiscount) offer creation for a run's eligible variants.
    // Validate-only DRY RUN by default (builds + validates the offer package,
    // never POSTs); a live submit requires `?confirm=true` and drives the
    // create → upload → mark-Ready package state machine. Octopia-platform only.
    octopiaOffers: "/:slug/octopia-offers",
    // Deterministic, category-aware image reconciliation used by `runs heal`.
    // Dry-run plans only; the route can never approve or upload.
    listingImagesReconcile: "/:slug/listing-images/reconcile",
    validate: "/:slug/validate",
    // Start or reattach to one durable extraction operation.
    extract: "/:slug/extract",
    extractOperation: "/:slug/extract-operations/:id",
    extractOperationClaim: "/:slug/extract-operations/:id/claim",
    extractOperationHeartbeat: "/:slug/extract-operations/:id/heartbeat",
    extractOperationResult: "/:slug/extract-operations/:id/items/:configSku/result",
    // Pure, no-model re-apply: re-validate + write-through each config's already
    // persisted extract payload. No model call.
    extractReapply: "/:slug/extract/reapply",
    // Exact cleanup of one persisted override attribute across a run. Preview
    // by default; commits echo the preview count and re-materialize affected
    // configs so the value store cannot keep the removed override.
    attributeOverridesClear: "/:slug/attribute-overrides/clear",
    approve: "/:slug/approve",
    // Per-run "set aside" bucket: park configs with a missing-data gap so the
    // rest of the run can build, then restore them once the data team fills it.
    setAside: "/:slug/set-aside",
    restore: "/:slug/restore",
    // Durable per-product set-aside note keyed on (configSku, marketplace) — set,
    // edit, or clear (empty note) it independent of the run. Survives re-runs;
    // the run only supplies the marketplace. First web-mutable set-aside surface.
    setAsideNote: "/:slug/set-aside/note",
    // Actionable missing-data list for the data team: one JSON row per active
    // gap config (configSku, name, ean, missingAttributes, gapReasons, …); the
    // CLI serializes it to CSV.
    exportGaps: "/:slug/export-gaps",
    cancel: "/:slug/cancel",
    fail: "/:slug/fail",
    // Backfill a run imported outside nova-ai → terminal `imported` status.
    markImported: "/:slug/mark-imported",
    // Explicitly return an imported run, or abandon an active import, to open.
    // Clears approval; preserves feed/import history. Never called by heal.
    reopen: "/:slug/reopen",
    archive: "/:slug/archive",
    unarchive: "/:slug/unarchive",
  }),
  skus: group("/skus", {
    list: "",
    stats: "/stats",
    // Config SKUs whose category disagrees across shared-tree sibling
    // marketplaces (the Decathlon cz/pl/it family). Static suffix, registered
    // before `detail`/`setCategory` so its single segment can't be captured as
    // a `:marketplace`.
    categoryDrift: "/category-drift",
    compare: "/compare",
    register: "/register",
    sync: "/sync",
    detail: "/:marketplace/:configSku",
    setCategory: "/:marketplace/:configSku/category",
    attributes: "/:marketplace/:sku/attributes",
    // Bulk validation is a separate capability from bulk mutation. A CLI built
    // with validation support therefore gets a 404 from an older server instead
    // of sending a preview flag to a write endpoint that might ignore it.
    attributesBatchValidation: "/:marketplace/attributes/batch/validation",
    // Bulk attribute write: one batched, partial-tolerant request that applies
    // many per-SKU writes (the batched twin of `attributes`).
    attributesBatch: "/:marketplace/attributes/batch",
  }),
  marketplaces: group("/marketplaces", {
    list: "",
    summary: "/summary",
    categories: "/:marketplace/categories",
    hierarchies: "/:marketplace/hierarchies",
    hierarchySchema: "/:marketplace/hierarchies/:code/schema",
    valueList: "/:marketplace/value-lists/:name",
    enums: "/:marketplace/enums/:name",
    categorySchema: "/:marketplace/categories/:slug/schema",
    dashboard: "/:marketplace/dashboard",
    // One row per ADVERTISED product over the CARD's own period (#482): the two
    // pairs an operator decides a bid from — ROAS against its break-even, bid
    // against its break-even — plus the traffic behind them. A separate read from
    // `dashboard` (offer-health pattern) so the card loads on its own, and it
    // carries its own window rather than the page's.
    campaignTable: "/:marketplace/campaign-table",
    campaignActionCapability: "/:marketplace/campaign-action-capability",
    campaignActionPlans: "/:marketplace/campaign-action-plans",
    campaignActionPlan: "/:marketplace/campaign-action-plans/:planId",
    campaignActionPlanStaging: "/:marketplace/campaign-action-plans/:planId/staging",
    campaignActionPlanApproval: "/:marketplace/campaign-action-plans/:planId/approval",
    campaignActionPlanClaim: "/:marketplace/campaign-action-plans/:planId/claim",
    campaignActionPlanHeartbeat: "/:marketplace/campaign-action-plans/:planId/heartbeat",
    campaignActionPlanActionPermit:
      "/:marketplace/campaign-action-plans/:planId/actions/:actionId/permit",
    campaignActionPlanResult: "/:marketplace/campaign-action-plans/:planId/result",
    // Top products by ORGANIC sales over the page's period (#512) — line sales
    // minus what the advertiser report attributes to each SKU. A separate read
    // from `dashboard` (offer-health pattern) so the table loads on its own and
    // a slow ad-fact aggregate never holds up the revenue chart above it.
    organicProducts: "/:marketplace/organic-products",
    // Fee-aware PnL (#167): window totals + per-day aggregates + per-product
    // breakdown, product contribution and fixed marketplace overhead returned
    // separately so totals reconcile. A separate read from the revenue dashboard
    // (offer-health pattern) so the section loads on its own.
    pnl: "/:marketplace/pnl",
    // Persisted, vendor-neutral payout history. The GET is mirror-only for
    // predictable reads; the sync endpoints refresh a bounded range from the
    // marketplace adapter. `payoutsSyncAll` is the scheduled sweep over every
    // payout-capable marketplace (static path first, so it isn't read as a
    // marketplace id); `payoutsSync` refreshes one (the dashboard's button).
    payoutsSyncAll: "/payouts/sync",
    // The fleet reads behind `/payouts`: every configured marketplace's cycles for
    // one settlement month, and every marketplace's account position. Static paths,
    // so like the sweep above they MUST be registered before the `:marketplace`
    // patterns — `/marketplaces/payouts/balances` otherwise matches
    // `/:marketplace/payouts` with a marketplace literally named "payouts".
    payoutsFleet: "/payouts",
    payoutBalancesFleet: "/payouts/balances",
    payouts: "/:marketplace/payouts",
    payoutsSync: "/:marketplace/payouts/sync",
    // The account's money position as of the newest snapshot. A SEPARATE read
    // from `payouts` on purpose: that one answers "what happened in this
    // window", this one answers "where do we stand" and takes no range at all —
    // folding a period-independent figure into a period-scoped response is
    // exactly the confusion this pair exists to keep apart.
    payoutBalance: "/:marketplace/payouts/balance",
    // Standalone PnL rebuild for a fee-model version bump / backfill over
    // already-imported history. Import materializes PnL inline; this re-runs the
    // same compute over stored facts. `nova ads recompute-pnl` wraps it.
    pnlRecompute: "/:marketplace/pnl/recompute",
    pnlCostEvidence: "/:marketplace/pnl/costs",
    pnlCostPublication: "/:marketplace/pnl/costs/publications/:id",
    pnlCostPreview: "/:marketplace/pnl/costs/preview",
    pnlCostConfirm: "/:marketplace/pnl/costs/confirm",
    pnlCostPause: "/:marketplace/pnl/costs/pause",
    ordersSync: "/:marketplace/orders/sync",
    // Refetch this marketplace's cached category metadata (attributes + the
    // value lists they reference) from the marketplace and OVERWRITE it — no age
    // threshold, no skip-if-present. Body `{ "hierarchy": "<code>" }` refreshes
    // one category and its ancestors whatever their `attributes_synced` flags
    // say; no body refreshes every category already cached for the schema
    // source. The only path that re-caches metadata: no read endpoint fetches
    // one mid-request (issue #278). `nova marketplaces refresh` wraps it.
    metadataRefresh: "/:marketplace/metadata/refresh",
    importStatus: "/:marketplace/imports/:runId/status",
    importRepairWorklist: "/:marketplace/imports/repair/worklist",
    importRepairFamily: "/:marketplace/imports/repair/family",
    importRepairPreview: "/:marketplace/imports/repair/preview",
    importRepairConfirm: "/:marketplace/imports/repair/confirm",
    importRepairStatus: "/:marketplace/imports/repair/status",
    importStatusSweep: "/import-status-sweep",
    // Bounded phase-A smoke: claim only existing accepted due imports and
    // launch the status-only worker. It cannot build, repair, or upload.
    importPollingSmoke: "/import-polling-smoke",
    importErrorReport: "/:marketplace/imports/:runId/error-report",
    // The per-IDENTIFIER worklist for one product import: pull both error-report
    // kinds, resolve each errored row's declared identifier back to a catalog
    // product, and report one entry per product instead of one per feed line.
    // Reports only — it never rebuilds a feed and never re-uploads (issue #269
    // item 4). `nova imports worklist` wraps it.
    importWorklist: "/:marketplace/imports/:runId/worklist",
    // Advertiser-report import (#166): raw CSV in, validated write to
    // marketplace_ad_reports_daily, structured validation report out. The API
    // owns parsing + validation; `nova ads import` wraps this.
    adReportImport: "/:marketplace/ad-report/import",
    // Read-back of imported ad facts at day × campaign grain. Accepts either
    // `?days=N` ending today or a paired exact `?from=YYYY-MM-DD&to=YYYY-MM-DD`.
    // Results are pre-grouped per campaign with per-day series + totals — the
    // trend surface the ads-review agent reasons over. `nova ads history` wraps it.
    adReportHistory: "/:marketplace/ad-report/history",
    // The deterministic profitability verdict over those same imported facts.
    // Accepts either `?days=N` ending today or paired exact `?from` and `?to`.
    // Returns per campaign × product CVR-derived bid ceilings, recommendations,
    // and the portfolio roll-up. No portal read. `nova ads profitability` wraps it.
    adReportProfitability: "/:marketplace/ad-report/profitability",
    // Immutable DB-first agent analysis. POST validates a complete campaign
    // snapshot, independently re-derives deterministic gates, and stores one
    // typed run. GET returns one run or the latest successful/latest attempt.
    adReportAnalysis: "/:marketplace/ad-report/analysis",
    // OF01 offer upload (operator-gated) and its status poll.
    offerImportCreate: "/:marketplace/offer-imports",
    offerImportStatus: "/:marketplace/offer-imports/:importId/status",
    // OF03 error report for a terminal import with hasErrorReport: raw Mirakl
    // CSV by default, `?format=json` for rows mapped to SKU + human-readable
    // error. The client scopes the read by the shop's configured shop_id.
    offerImportErrorReport: "/:marketplace/offer-imports/:importId/error-report",
    // One step of the offer recovery loop: download + parse the error report,
    // apply supported error-targeted fixes, re-upload ONLY corrected rows as a
    // NEW import, and report the unfixable rows per SKU. Fixes are idempotent
    // error-targeted normalizers, so the upload-driven status → recover loop
    // terminates instead of cycling.
    offerImportRecover: "/:marketplace/offer-imports/:importId/recover",
    // Poll an Octopia (Cdiscount) offer-package's per-offer Integrated/Rejected
    // results, normalized to the platform-neutral ImportStatus. The verify step
    // of the initial-creation loop (`runs.octopiaOffers` submits, this reads)
    // and of the selective update (`octopiaOfferUpdates` submits, this reads).
    octopiaOfferResults: "/:marketplace/octopia-offer-packages/:packageId/results",
    // Selective Octopia (Cdiscount) offer UPDATE for EXACT seller SKUs (issue
    // #377): `?skus=A,B&preparationTime=1[&confirm=true]`. Read-only dry run by
    // default (current vs requested value + the exact outbound JSON, no write
    // endpoint call); `?confirm=true` opens an Update package with exactly the
    // reviewed requests and returns its packageId for `octopiaOfferResults`.
    octopiaOfferUpdates: "/:marketplace/octopia-offer-updates",
    // Persisted offer snapshot: the count of shop_skus the marketplace already
    // carries + when it was last synced. A pure DB read (mirror-only); the live
    // walk that fills it runs only in the offer-sync job. The new-only offer
    // diff reads it.
    offerSnapshot: "/:marketplace/offer-snapshot",
    // Build the marketplace-WIDE OF01 offer CSV: the union of every run's
    // eligible variants, diffed once against the persisted snapshot. Run-agnostic
    // sibling of `runs.offerFeed`. Build only, no upload.
    offerFeed: "/:marketplace/offer-feed",
    // Offer health (active/inactive coverage + inactivity-reason histogram),
    // read from persisted snapshots. `offerHealth` is the all-marketplaces
    // rollup (latest per marketplace); `offerHealthByMarketplace` adds the
    // per-marketplace trend.
    //
    // The live /api/offers walk that refreshes BOTH the coverage snapshot and
    // the health series is a background JOB (it spans minutes — past any request
    // timeout): `offerSyncJobs` (POST) syncs every offer-readable marketplace
    // (daily cron), `offerSyncJobsByMarketplace` (POST) syncs one (the dashboard
    // "Sync offers" button), and `offerSyncJob` (GET) polls either by id. Static
    // paths are declared before the `:marketplace` ones so they register first
    // and aren't swallowed as a marketplace id.
    offerHealth: "/offer-health",
    offerSyncJobs: "/offer-sync-jobs",
    offerSyncJob: "/offer-sync-jobs/:id",
    offerSyncJobsByMarketplace: "/:marketplace/offer-sync-jobs",
    offerHealthByMarketplace: "/:marketplace/offer-health",
    // One SKU's health as of the latest snapshot: online or not, and the score
    // reasons behind it (issue #279). Registered after the marketplace-level
    // offer-health route so the plain path isn't read as a SKU.
    offerHealthBySku: "/:marketplace/offer-health/:sku",
    // Baselinker PANEL storage refresh — push the assigned storage's price/QTY
    // onto a shop's live offers (the dashboard's per-shop button). Two phases,
    // both background JOBS: enumerating a whole account, and then waiting for
    // QTY to converge, each outlive a request. `baselinkerRefresh` (GET) reports
    // whether the shop can be refreshed at all plus its last run,
    // `baselinkerRefreshJobs` (POST) starts a preview or a commit, and
    // `baselinkerRefreshJob` (GET) polls one by id — declared before the
    // `:marketplace` form so the static path registers first.
    baselinkerRefreshJob: "/baselinker-refresh-jobs/:id",
    baselinkerRefresh: "/:marketplace/baselinker-refresh",
    baselinkerRefreshJobs: "/:marketplace/baselinker-refresh-jobs",
  }),
  orders: group("/orders", {
    list: "",
    counts: "/counts",
    magentoFreshness: "/magento-freshness",
    latestSync: "/sync-jobs/latest",
    syncJob: "/sync-jobs/:id",
    syncMarketplaces: "/sync/marketplaces",
    enrichBaselinker: "/enrich/baselinker",
    enrichMagento: "/enrich/magento",
    enrichOms: "/enrich/oms",
    detect: "/detect",
    detail: "/:marketplace/:orderId",
    address: "/:marketplace/:orderId/address",
    investigate: "/:marketplace/:orderId/investigate",
    investigation: "/:marketplace/:orderId/investigation",
    magentoAttempts: "/:marketplace/:orderId/magento-attempts",
    selectMagentoAttempt: "/:marketplace/:orderId/select-magento-attempt",
    repairProposal: "/:marketplace/:orderId/repair-address/proposal",
    repairAddress: "/:marketplace/:orderId/repair-address",
    resolve: "/:marketplace/:orderId/resolve",
  }),
  scheduledJobs: group("/scheduled-jobs", {
    list: "",
    preview: "/:id/preview",
    revisions: "/:id/revisions",
    runs: "/:id/runs",
    runNow: "/:id/run-now",
    update: "/:id",
    detail: "/:id",
  }),
  scheduledJobRuns: group("/scheduled-job-runs", {
    queue: "",
    detail: "/:id",
  }),
  scheduledJobDispatcher: group("/internal/scheduled-jobs", {
    dispatch: "/dispatch",
  }),
  agentEvents: group("/agent-events/orders-interpreter", {
    claim: "/claim",
    release: "/release",
    interpretation: "/events/:eventId/interpretation",
    interpretations: "/events/:eventId/interpretations",
  }),
  feeds: group("/feeds", {
    build: "",
    familyMappingPreview: "/family-mapping-preview",
    completeBuildJobs: "/complete-build-jobs",
    completeBuildJob: "/complete-build-jobs/:id",
    download: "/:id/download",
  }),
  users: group("/users", {
    list: "",
    role: "/:id/role",
  }),
  products: group("/products", {
    // Static collection route. Product routers must register it before the
    // `/:sku` catch-all owned by the integration read surface.
    list: "",
    sync: "/sync",
    freshness: "/freshness",
    staleRecovery: "/stale-recovery",
    costCoverage: "/cost-history/coverage",
    costBaseline: "/cost-history/baseline",
    costCapture: "/cost-history/capture",
    costIdentities: "/:sku/cost-identities",
    costHistory: "/:sku/cost-history",
    values: "/:sku/values",
    // Config-scoped product-visual gallery (issue #251): every live simple's
    // per-kind Current/Stale/New/Gap/Excluded state for one store.
    visuals: "/:sku/visuals",
    productPhotos: "/:sku/product-photos",
    categorySkus: "/category-skus",
    commercials: "/commercials",
    detail: "/:sku",
    facts: "/:sku/facts",
  }),
  productValues: group("/product-values", {
    recoveryPlan: "/:marketplace/recovery-plan",
    recoveryStatus: "/:marketplace/recovery-status",
    recoveryControls: "/:marketplace/recovery-controls",
    recoveryChanges: "/:marketplace/recovery-changes",
    // Immutable typed-writer candidate generations. Build never changes the
    // active feed source; reads expose progress, failures and assortment impact.
    generations: "/generations",
    generation: "/generations/:id",
    activationPreview: "/activation/preview",
    activate: "/activation",
    activeState: "/active/:marketplace",
    activeValues: "/active/:marketplace/values",
    events: "/active/:marketplace/events",
    rollback: "/active/:marketplace/rollback",
  }),
  productVisuals: group("/product-visuals", {
    nutritionImages: "/nutrition-images",
    health: "/health",
    trace: "/trace",
    pimCompletionFailures: "/pim-completion/failures",
    // Batch render surface. Static suffixes registered before the `/:store`
    // preview catch-all so they are never shadowed by it.
    renderPlans: "/render/plans",
    exactBatchPreview: "/render/exact/preview",
    exactBatchExecute: "/render/exact/:id/execute",
    exactBatch: "/render/exact/:id",
    renderPlan: "/render/plans/:id",
    renderJobs: "/render/jobs",
    renderJob: "/render/jobs/:id",
    fillSettlementPreview: "/fill/settlements/preview",
    fillSettlementExecute: "/fill/settlements/execute",
    fillSettlement: "/fill/settlements/:id",
    fillAdmission: "/fill/admission",
    fillJobs: "/fill/jobs",
    fillJob: "/fill/jobs/:id",
    pimBaselineBatches: "/pim-baseline/batches",
    generationJobs: "/generation/jobs",
    generationJob: "/generation/jobs/:id",
    generationStop: "/generation/jobs/:id/stop",
    images: "/images",
    worklist: "/worklist",
    replacementRecovery: "/replacements/recovery",
    interruptionRecovery: "/replacements/interruption-recovery",
    replacement: "/:store/:simpleSku/replacements",
    cutout: "/:store/:simpleSku/cutout",
    cutoutJob: "/cutout-jobs/:id",
    photoAssignments: "/:store/:simpleSku/photo-assignments",
    preview: "/:store/:simpleSku/preview",
  }),
  galleryStudio: group("/gallery-studio", {
    paidWorkControl: "/paid-work-control",
    intents: "/intents",
    intent: "/intents/:configSku",
    bootstrapInputs: "/intents/:configSku/bootstrap-inputs",
    planInputs: "/intents/:configSku/plan-inputs",
    plans: "/intents/:configSku/plans",
    launchPreview: "/intents/:configSku/launch-preview",
    draftJobs: "/intents/:configSku/draft-jobs",
    draftJob: "/intents/:configSku/draft-jobs/:jobId",
    cancelDraftJob: "/intents/:configSku/draft-jobs/:jobId/cancel",
    slots: "/intents/:configSku/slots",
    currentDocument: "/intents/:configSku/documents/current",
    documentRevisions: "/intents/:configSku/documents/:documentId/revisions",
    documentCandidates: "/intents/:configSku/documents/:documentId/candidates",
    revisionCandidates: "/intents/:configSku/revisions/:revisionId/candidates",
    saveDocument: "/intents/:configSku/documents",
    renderCandidate: "/intents/:configSku/candidates",
    selectCandidate: "/intents/:configSku/selections",
    applyToVariants: "/intents/:configSku/apply-to-variants",
    approve: "/intents/:configSku/approvals",
    retryProjection: "/intents/:configSku/approvals/:approvalId/retry-projection",
    identityContracts: "/intents/:configSku/identity-contracts",
    identityAnchorAttempts: "/intents/:configSku/identity-contracts/:contractId/anchor-attempts",
    approveIdentityAnchor:
      "/intents/:configSku/identity-contracts/:contractId/anchor-attempts/:attemptId/approve",
    familyMasters: "/intents/:configSku/family-masters",
    sceneRequests: "/intents/:configSku/scene-requests",
    sceneActivationSmokes: "/intents/:configSku/scene-activation-smokes",
    retrySceneActivationSmokeAnalysis:
      "/intents/:configSku/scene-activation-smokes/:plateId/retry-analysis",
    sceneRequest: "/intents/:configSku/scene-requests/:requestId",
    regenerateScene: "/intents/:configSku/scene-requests/:requestId/regenerate",
    retrySceneAnalysis: "/intents/:configSku/scene-plates/:plateId/retry-analysis",
    useScene: "/intents/:configSku/scene-plates/:plateId/use",
    rejectScene: "/intents/:configSku/scene-plates/:plateId/reject",
    historicalBackgroundRequest: "/intents/:configSku/historical-background-requests/:requestId",
  }),
  visualDirections: group("/visual-directions", {
    list: "",
    detail: "/:directionId",
    draft: "/:directionId/draft",
    assets: "/:directionId/assets",
    asset: "/:directionId/assets/:assetId",
    assetContent: "/assets/:assetId/content",
    assetGlyphCoverage: "/assets/:assetId/glyph-coverage",
    publish: "/:directionId/publish",
    archive: "/:directionId/archive",
    archiveVersion: "/:directionId/versions/:versionId/archive",
    proposals: "/:directionId/proposals",
    proposal: "/:directionId/proposals/:proposalId",
    abandonProposal: "/:directionId/proposals/:proposalId/abandon",
    applyProposal: "/:directionId/proposals/:proposalId/apply",
    moodboardSample: "/:directionId/moodboard-sample",
    brandDefault: "/defaults/:brandFamily",
    productSelection: "/products/:configSku/selection",
  }),
  productClaims: group("/product-claims", {
    propose: "",
    validateSelection: "/selections/validate",
    wordings: "/:claimVersionId/wordings",
    read: "/:marketplace/:simpleSku",
  }),
  searchMarketplaces: group("/search-marketplaces", {
    list: "",
  }),
  searchRankings: group("/search-rankings/:marketplace", {
    overview: "",
    importRun: "/runs/import",
    keyword: "/keywords/:phrase",
    keywordOpportunities: "/keywords/:phrase/opportunities",
    productRankings: "/products/:sku/rankings",
  }),
  rates: group("/rates", {
    list: "",
  }),
  stores: group("/stores", {
    listingImagesRender: "/:store/listing-images/render",
    listingImagesWireNutrition: "/:store/listing-images/wire-nutrition",
    listingImagesWireSize: "/:store/listing-images/wire-size",
    listingImagesClearNutrition: "/:store/listing-images/clear-nutrition",
  }),
  shopApotheke: group("/shop-apotheke", {
    listingImagesLinkWhiteGallery: "/runs/:slug/listing-images/link-white-gallery",
  }),
  // QA-cron command surface. Gated to the `system:nova-qa` principal (or admin);
  // the nightly grid creates/reaps qa-origin runs and writes/reads per-cell
  // eval results through these. Marketplace-neutral — no vendor names.
  qa: group("/qa", {
    reap: "/reap",
    evalResults: "/eval-results",
    precheck: "/precheck",
    ordersAddressCorrectionDemo: "/orders/address-correction-demo",
  }),
}
